import { inject, Injectable, OnDestroy } from '@angular/core';
import { EntityHttpResourceUrls, HttpResourceUrls, HttpUrlGenerator, normalizeRoot, Pluralizer } from '@ngrx/data';
import { map, shareReplay } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { CcoEntityUrlRegistryService } from '@app/data/cco-entity-url-registry.service';
import { userTenantFeature } from '@core/user-tenant/user-tenant.reducer';
import { notNullish } from '@app/shared/rxjs/operators/standard-operators';
import { Store } from '@ngrx/store';

const GLOBAL_ENTITIES = [
  'GoogleCalendar',
  'LiveBatch',
  'PlcBatchRecord',
];

@Injectable()
export class CcoHttpUrlGenerator extends HttpUrlGenerator implements OnDestroy {
  private readonly tenantId$ = inject(Store)
    .select(userTenantFeature.selectSelectedTenantId)
    .pipe(notNullish(), shareReplay());
  protected destroyed$ = new Subject<void>();

  protected globalScopedKnownHttpResourceUrls: EntityHttpResourceUrls = {};
  protected tenantScopedKnownHttpResourceUrls: { [tenantId: string]: EntityHttpResourceUrls } = {};

  constructor(private myPluralizer: Pluralizer, private entityUrlRegistry: CcoEntityUrlRegistryService) {
    super();
  }

  /**
   * Create the path to a single entity resource
   * @param entityName {string} Name of the entity type, e.g, 'Hero'
   * @param root {string} Root path to the resource, e.g., 'some-api`
   * @returns complete path to resource, e.g, 'some-api/hero'
   */
  entityResource(entityName: string, root: string): string {
    return this.getResourceUrls(entityName, root).entityResourceUrl;
  }

  entityResource$(entityName:string, root:string): Observable<string> {
    return this.tenantId$.pipe(
      map(tenantId => this.getResourceUrls(entityName, root, tenantId).entityResourceUrl)
    );
  }

  collectionResource$(entityName: string, root: string): Observable<string> {
    return this.tenantId$.pipe(
      map(tenantId => this.getResourceUrls(entityName, root, tenantId).collectionResourceUrl)
    );
  }

  /**
   * Create the path to a multiple entity (collection) resource
   * @param entityName {string} Name of the entity type, e.g, 'Hero'
   * @param root {string} Root path to the resource, e.g., 'some-api`
   * @returns complete path to resource, e.g, 'some-api/heroes'
   */
  collectionResource(entityName: string, root: string): string {
    return this.getResourceUrls(entityName, root).collectionResourceUrl;
  }

  /**
   * Register known single-entity and collection resource URLs for HTTP calls
   * @param entityHttpResourceUrls {EntityHttpResourceUrls} resource urls for specific entity type names
   * @param tenantId for tenant scoped resources
   * Well-formed resource urls end in a '/';
   * Note: this method does not ensure that resource urls are well-formed.
   */
  registerHttpResourceUrls(
    entityHttpResourceUrls: EntityHttpResourceUrls,
    tenantId?: string,
  ): void {
    if (tenantId != null) {
      if (!this.tenantScopedKnownHttpResourceUrls[tenantId]) {
        this.tenantScopedKnownHttpResourceUrls[tenantId] = {};
      }

      this.tenantScopedKnownHttpResourceUrls[tenantId] = {
        ...this.tenantScopedKnownHttpResourceUrls[tenantId],
        ...(entityHttpResourceUrls || {}),
      };
    } else {
      this.globalScopedKnownHttpResourceUrls = {
        ...this.globalScopedKnownHttpResourceUrls,
        ...(entityHttpResourceUrls || {}),
      };
    }
  }


  /**
   * Get or generate the entity and collection resource URLs for the given entity type name
   * @param entityName Name of the entity type, e.g, 'Hero'
   * @param root Root path to the resource, e.g., 'some-api`
   * @param tenantId for tenant-scoped
   */
  protected getResourceUrls(entityName: string, root: string, tenantId?: string): HttpResourceUrls {
    if (tenantId != null) {
      if (!this.tenantScopedKnownHttpResourceUrls[tenantId]) {
        this.tenantScopedKnownHttpResourceUrls[tenantId] = {};
      }
    }

    let resourceUrls = tenantId != null ? this.tenantScopedKnownHttpResourceUrls[tenantId][entityName] : this.globalScopedKnownHttpResourceUrls[entityName];
    if (!resourceUrls) {
      const nRoot = normalizeRoot(root) + (GLOBAL_ENTITIES.includes(entityName) ? '' : `/o/${tenantId}`);
      const entityUrlMap = this.entityUrlRegistry.getEntityUrlMap();
      if (entityUrlMap[entityName]) {
        resourceUrls = {
          entityResourceUrl: `${nRoot}/${entityUrlMap[entityName].entityResourceUrl}/`,
          collectionResourceUrl: `${nRoot}/${entityUrlMap[entityName].collectionResourceUrl}`,
        };
      } else {
        resourceUrls = {
          entityResourceUrl: `${ nRoot }/${ toKebabCase(
            this.myPluralizer.pluralize(entityName),
          ).toLowerCase() }/`,
          collectionResourceUrl: `${ nRoot }/${ toKebabCase(
            this.myPluralizer.pluralize(entityName),
          ).toLowerCase() }`,
        };
      }
      this.registerHttpResourceUrls({ [entityName]: resourceUrls }, tenantId);
    }
    return resourceUrls;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

export function toKebabCase(str: string): string {
  return (
    str &&
    str
      .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
      .map(x => x.toLowerCase())
      .join('-')
  );
}
